import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Users, ChevronUp, ChevronDown, Download, Clock, Award } from 'lucide-react';
import { ColDef, GridApi, GridReadyEvent, ValueFormatterParams } from 'ag-grid-community';
import { parseISO, format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePagination } from '@/hooks/usePagination';
import { userAdminService, profileService, type UserProfileRow } from '@/services';
import { generateCsv, downloadCsv, getVisibleColumnsFromGrid } from '@/lib/export';
import { AGDataGrid } from '@/components/ui/ag-data-grid';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import GridExportDialog, { type ExportScope } from '@/components/export/GridExportDialog';
import UserDetailDialog from '@/components/security/UserDetailDialog';
import { toast } from 'sonner';

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy h:mm a');
  } catch {
    return '\u2014';
  }
}

function getInitials(name: string | null): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export default function SecurityUsers() {
  const { user } = useAuth();

  const {
    pageIndex,
    pageSize,
    searchValue,
    debouncedSearch,
    setSearchValue,
    setPageIndex,
    setPageSize,
    pageSizeOptions,
  } = usePagination({ defaultPageSize: 25, storageKey: 'security-users' });

  const [companyId, setCompanyId] = useState<string | null>(null);
  const [allUsers, setAllUsers] = useState<UserProfileRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isHeaderOpen, setIsHeaderOpen] = useState(true);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfileRow | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string>>({});
  const gridApiRef = useRef<GridApi | null>(null);

  // Fetch company ID on mount
  useEffect(() => {
    const fetchCompanyId = async () => {
      if (!user) return;
      const { data: profile } = await profileService.get(user.id);
      if (profile?.company_id) {
        setCompanyId(profile.company_id);
      } else {
        setIsLoading(false);
      }
    };
    fetchCompanyId();
  }, [user]);

  // Fetch all users when companyId is available
  useEffect(() => {
    const fetchUsers = async () => {
      if (!companyId) return;
      setIsLoading(true);
      const result = await userAdminService.listUsers(companyId);
      if (result.error) {
        toast.error('Failed to load users');
      } else {
        setAllUsers(result.data);
      }
      setIsLoading(false);
    };
    fetchUsers();
  }, [companyId]);

  // Resolve signed avatar URLs for users with avatars
  useEffect(() => {
    const resolveAvatars = async () => {
      const usersWithAvatars = allUsers.filter(u => u.avatar_url);
      if (usersWithAvatars.length === 0) return;

      const urls: Record<string, string> = {};
      await Promise.all(
        usersWithAvatars.map(async (u) => {
          const { data } = await supabase.storage
            .from('avatars')
            .createSignedUrl(u.avatar_url!, 3600);
          if (data?.signedUrl) urls[u.id] = data.signedUrl;
        })
      );
      setAvatarUrls(urls);
    };
    resolveAvatars();
  }, [allUsers]);

  // Active users count metric
  const { data: activeUserCount, isLoading: isCountLoading } = useQuery({
    queryKey: ['security-active-users', companyId],
    queryFn: async () => {
      const result = await userAdminService.countActiveUsers(companyId!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  });

  // Client-side search filtering
  const filteredUsers = useMemo(() => {
    if (!debouncedSearch) return allUsers;
    const search = debouncedSearch.toLowerCase();
    return allUsers.filter(
      (u) =>
        u.full_name?.toLowerCase().includes(search) ||
        u.email?.toLowerCase().includes(search)
    );
  }, [allUsers, debouncedSearch]);

  // Client-side pagination
  const paginatedUsers = useMemo(() => {
    const start = pageIndex * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, pageIndex, pageSize]);

  const totalCount = filteredUsers.length;

  // Column definitions
  const columnDefs = useMemo<ColDef<UserProfileRow>[]>(
    () => [
      {
        headerName: '',
        field: 'avatar_url',
        width: 60,
        sortable: false,
        filter: false,
        resizable: false,
        suppressColumnsToolPanel: true,
        cellRenderer: (params: { data: UserProfileRow | undefined }) => {
          if (!params.data) return null;
          const initials = getInitials(params.data.full_name);
          return (
            <div className="flex items-center justify-center h-full">
              {avatarUrls[params.data.id] ? (
                <img
                  src={avatarUrls[params.data.id]}
                  alt={params.data.full_name ?? ''}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">
                  {initials}
                </div>
              )}
            </div>
          );
        },
      },
      {
        headerName: 'Full Name',
        field: 'full_name',
        flex: 1,
        minWidth: 180,
      },
      {
        headerName: 'Company',
        field: 'company_name',
        width: 180,
      },
      {
        headerName: 'Email',
        field: 'email',
        flex: 1,
        minWidth: 200,
      },
      {
        headerName: 'Last Login',
        field: 'last_sign_in_at',
        width: 170,
        valueFormatter: (params: ValueFormatterParams<UserProfileRow>) =>
          formatDateTime(params.value),
      },
      {
        headerName: 'Created',
        field: 'profile_created',
        width: 170,
        valueFormatter: (params: ValueFormatterParams<UserProfileRow>) =>
          formatDateTime(params.value),
      },
      {
        headerName: 'Modified',
        field: 'profile_modified',
        width: 170,
        valueFormatter: (params: ValueFormatterParams<UserProfileRow>) =>
          formatDateTime(params.value),
      },
    ],
    [avatarUrls]
  );

  const handleGridReady = useCallback((event: GridReadyEvent<UserProfileRow>) => {
    gridApiRef.current = event.api;
  }, []);

  const handleRowClicked = useCallback((data: UserProfileRow) => {
    setSelectedUser(data);
    setDetailDialogOpen(true);
  }, []);

  const handleExport = useCallback(
    async (scope: ExportScope) => {
      if (!companyId) return;
      setIsExporting(true);
      try {
        const rows = scope === 'all' ? allUsers : filteredUsers;

        const columns = gridApiRef.current
          ? getVisibleColumnsFromGrid(gridApiRef.current)
          : [
              { field: 'full_name', headerName: 'Full Name' },
              { field: 'company_name', headerName: 'Company' },
              { field: 'email', headerName: 'Email' },
              { field: 'last_sign_in_at', headerName: 'Last Login' },
              { field: 'profile_created', headerName: 'Created' },
              { field: 'profile_modified', headerName: 'Modified' },
            ];

        const csv = generateCsv(rows as Record<string, unknown>[], columns);
        const suffix = scope === 'all' ? 'all' : 'filtered';
        downloadCsv(`users-${suffix}_${new Date().toISOString().split('T')[0]}.csv`, csv);
        setExportDialogOpen(false);
      } catch (error) {
        console.error('Export failed:', error);
        toast.error('Failed to export users');
      } finally {
        setIsExporting(false);
      }
    },
    [companyId, allUsers, filteredUsers]
  );

  return (
    <div className="animate-slide-up space-y-4">
      {/* Collapsible header with metric cards */}
      <Collapsible open={isHeaderOpen} onOpenChange={setIsHeaderOpen}>
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between py-2">
            <div>
              <h1 className="page-title text-left">Users</h1>
              <p className="text-sm text-muted-foreground text-left">
                Manage and monitor user accounts.
              </p>
            </div>
            {isHeaderOpen ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 pb-4">
            {/* Total Users Card */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isCountLoading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <div className="text-2xl font-bold">
                    {activeUserCount?.toLocaleString() ?? 0}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">Active accounts</p>
              </CardContent>
            </Card>

            {/* Licensed Users Placeholder */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Licensed Users</CardTitle>
                <Award className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">Coming soon</p>
              </CardContent>
            </Card>

            {/* Top 5 Most Active Placeholder */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Top 5 Most Active</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">Coming soon</p>
              </CardContent>
            </Card>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* User list grid */}
      <AGDataGrid<UserProfileRow>
        rowData={paginatedUsers}
        columnDefs={columnDefs}
        totalCount={totalCount}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        searchValue={searchValue}
        searchPlaceholder="Search by name or email..."
        isLoading={isLoading}
        onSearchChange={setSearchValue}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        onGridReady={handleGridReady}
        onRowClicked={handleRowClicked}
        toolbarActions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExportDialogOpen(true)}
            disabled={totalCount === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        }
      />

      <GridExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        onExport={handleExport}
        selectedCount={0}
        hasActiveFilter={!!debouncedSearch}
        totalCount={allUsers.length}
        isExporting={isExporting}
        title="Export Users"
      />

      {companyId && (
        <UserDetailDialog
          open={detailDialogOpen}
          onOpenChange={setDetailDialogOpen}
          user={selectedUser}
          companyId={companyId}
        />
      )}
    </div>
  );
}
